import { useScreenSize } from '@/themes/media-query'
import { DataGrid } from 'devextreme-react'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoaderData } from 'react-router-typesafe'
import {
  Button as GridButton,
  Column,
  ColumnChooser,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Item,
  Pager,
  Scrolling,
  Search,
  SearchPanel,
  Selection,
  Sorting,
  StateStoring,
  Summary,
  Toolbar,
  TotalItem,
} from 'devextreme-react/data-grid'
import dxDataGrid, { ColumnButtonClickEvent, ExportingEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import ODataStore from 'devextreme/data/odata/store'
import notify from 'devextreme/ui/notify'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { spesa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { confirm } from 'devextreme/ui/dialog'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { ClickEvent } from 'devextreme/ui/button'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import {
  createDateFilterItemDescriptor,
  FormFilterItemDescriptorType,
} from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { TipologiaSpesa } from '@/routes/expenses/ExpenseEditor.types'
import { expensesPageRouteLoader } from '@/routes/expenses/ExpensesPage.route'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { getMainDocumentPreview } from '@/routes/expenses/utils'

const ExpensesPage = () => {
  const { title, impiegati, userInfo, defaultCRUDAllowedRoles } = useLoaderData<typeof expensesPageRouteLoader>()
  const impiegatoDefault = impiegati.data.value.find((impiegato) => impiegato.fullname === userInfo?.name)
  const token = useTokenRefresh()

  const currentScreenSize = useScreenSize()
  const gridRefSpese = useRef<DataGridRef>(null)
  const navigate = useNavigate()
  const { service, client } = useQsAdminApiManager()

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'impiegato',
        placeHolder: 'Impiegato',
        valueKeyName: 'id',
        valueDisplayExpr: 'fullname',
        defaultValue: impiegatoDefault?.id,
        values: impiegati.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['impiegato/id', this.currentValue]]
        },
      },
      createDateFilterItemDescriptor('data', 'Data', 'Mese corrente'),
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [impiegati.data.value, impiegatoDefault?.id])

  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const [currentFilter, setCurrentFilter] = useState<any[]>([])

  const getGridHeight = useCallback(() => {
    let h = '72vh'
    if (currentScreenSize.isMedium) {
      h = '68vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '65vh'
    }
    return h
  }, [currentScreenSize])

  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const deleteExpense = (expense: spesa) => {
    const result = confirm(`<i>Vuoi davvero eliminare la spesa <b>${expense.ded_Dis}</b>?</i>`, 'Eliminazione spesa')
    result.then((dialogResult) => {
      if (dialogResult === false) return
      service
        .spesa(expense.id)
        .delete()
        .then(() => {
          // Aggiornamento griglia
          gridRefSpese.current?.instance().refresh()

          notify(
            {
              message: `Spesa '${expense['id']}' eliminata con successo`,
              type: 'success',
              displayTime: 5000,
            },
            {
              position: 'bottom center',
              direction: 'up-push',
            },
          )
        })
        .catch((error: unknown) => {
          console.log('error', error)
          notify(
            {
              message: `Errore eliminazione spesa: ${error}`,
              type: 'error',
              displayTime: 5000,
            },
            {
              position: 'bottom center',
              direction: 'up-push',
            },
          )
        })
    })
  }
  const onDeleteExpenseClick = (expense?: spesa) => {
    if (expense) {
      deleteExpense(expense)
    } else {
      onDataGridSelectedRowButtonClick(gridRefSpese, (rowdata: spesa) => {
        deleteExpense(rowdata)
      })
    }
  }

  const onExporting = (e: ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet(e.element.id === 'hours-datagrid' ? 'Hours' : 'Expenses')
    const component: dxDataGrid = e.component
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          e.element.id === 'hours-datagrid' ? 'Hours.xlsx' : 'Expenses.xlsx',
        )
      })
    })
  }

  const calculateSpesa = (options: DataGridTypes.CustomSummaryInfo) => {
    if (options.summaryProcess === 'start') {
      options.totalValue = options.name === 'Spesa' ? { vitto: 0, trasferta: 0, alloggio: 0 } : 0
    }

    if (options.name === 'Spesa' && options.summaryProcess === 'calculate') {
      const row: spesa = options.value
      switch (row.tipologia?.id) {
        case TipologiaSpesa.VITTO:
          options.totalValue.vitto += row.totale
          break
        case TipologiaSpesa.ALLOGGIO:
          options.totalValue.alloggio += row.totale
          break
        case TipologiaSpesa.TRASFERTA:
          options.totalValue.trasferta += row.totale
          break
        default:
          break
      }
    }
  }

  const expensesDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/spesa`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: ['impiegato', 'tipologia'],
    filter: currentFilter?.length > 0 ? currentFilter : null,
    requireTotalCount: true,
  }

  useEffect(() => {
    getCurrentFilter()
  }, [])

  return (
    <>
      <div className="accordion-generic-filter-form">
        <Accordion
          className={filtersApplied ? 'with-filter' : ''}
          collapsible={true}
          onContentReady={(e: ContentReadyEvent<any, any>) => {
            e.component?.collapseItem(0)
          }}
        >
          <AccordionItem icon="filter" title={'Filtri'}>
            <GenericFilterForm
              FormItemDescriptors={filterConfiguration}
              resetFilterClicked={function (e: ClickEvent): void {
                setFilterConfiguration(getFiltersConfiguration())
              }}
              filterValueChanged={function (): void {
                const currentFlts = getCurrentFilter()
                console.log('currentFlts', currentFlts)
                console.log('GENFLT - VALUE CHNG', currentFlts)
                if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                  console.log('GENFLT - SETTING FILT', currentFlts)
                  setCurrentFilter(currentFlts)
                }
              }}
            />
          </AccordionItem>
        </Accordion>
      </div>
      <DataGrid
        id={'expenses-datagrid'}
        height={getGridHeight()}
        dataSource={expensesDataSource}
        noDataText="Nessuna spesa trovata"
        className={'dx-card wide-card'}
        showBorders={false}
        showColumnLines={true}
        wordWrapEnabled={false}
        showRowLines={true}
        focusedRowEnabled={true}
        rowAlternationEnabled={true}
        allowColumnResizing={true}
        allowColumnReordering={true}
        width="100%"
        onExporting={onExporting}
        ref={gridRefSpese}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        remoteOperations={{
          filtering: true,
          grouping: true,
          groupPaging: true,
          paging: true,
          sorting: true,
          summary: false,
        }}
        grouping={{
          // Fondamentale per il raggruppamento veloce!!!
          autoExpandAll: true,
        }}
        groupPanel={{
          visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
          emptyPanelText: 'Trascina qui una colonna per raggruppare',
        }}
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`${e.data.id}`)
        }}
      >
        <Pager
          visible={true}
          allowedPageSizes={'auto'}
          displayMode={'compact'}
          showPageSizeSelector={false}
          showInfo={true}
          showNavigationButtons={false}
          infoText={gridTotalItemsLabel}
        />
        <FilterRow visible={true} />
        <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 200 : 250} />
        <Sorting mode="multiple"></Sorting>
        <FilterPanel visible={true} />
        <Export enabled={true} />
        <Editing allowAdding={true} />
        <HeaderFilter visible={true} />
        <Selection mode="single" />
        <ColumnChooser enabled={true} />
        <Scrolling mode="virtual" />
        <StateStoring enabled={true} type="localStorage" storageKey={'expenses-datagrid'} savingTimeout={50} />
        <Column
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
          showInColumnChooser={false}
          visible={true}
        >
          <GridButton
            hint="Anteprima"
            icon="search"
            onClick={async (e: ColumnButtonClickEvent) => {
              gridRefSpese.current?.instance().beginCustomLoading('Anteprima scontrino')
              try {
                const data: spesa = e.row?.data
                await getMainDocumentPreview(data)
              } catch (error: any) {
                notify(`Errore anteprima scontrino. Dettagli: ${error}`, 'error', 2000)
              } finally {
                gridRefSpese.current?.instance().endCustomLoading()
              }
            }}
          ></GridButton>
        </Column>
        <Column
          dataField={'id'}
          caption="ID"
          visible={false}
          dataType="number"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'spesa', 'id', true)}></HeaderFilter>
        </Column>
        <Column
          dataField={'ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: spesa = cellData.row.data
            return <ObjectCodeLink url={`${data.id}`} text={data.ded_Dis}></ObjectCodeLink>
          }}
          caption="CODICE"
          visible={true}
          allowSearch={false}
          dataType="string"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 130 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 130 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'spesa', 'ded_Dis')}>
            <Search enabled={true} searchExpr={'ded_Dis'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'impiegato.fullname'}
          caption="IMPIEGATO"
          visible={false}
          dataType="string"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'user', 'fullname', true)}></HeaderFilter>
        </Column>
        {DateColumn({
          dataField: 'data',
          caption: 'DATA',
          width: 100,
          format: 'dd/MM/yyyy',
          defaultSortOrder: 'desc',
          hidingPriority:
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined,
        })}
        <Column
          dataField={'tipologia.nome'}
          caption="TIPOLOGIA"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 70 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 70 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'tipologia_spesa', 'nome')}>
            <Search enabled={true} searchExpr={'nome'} />
          </HeaderFilter>
        </Column>
        <Column
          dataField={'note'}
          caption="NOTE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 150 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 150 : undefined}
        ></Column>
        <Column
          dataField={'totale'}
          caption="TOTALE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 50 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 50 : undefined}
        >
          <HeaderFilter dataSource={columnSourceFactory(token, 'spesa', 'totale', true)}></HeaderFilter>
        </Column>
        <Summary calculateCustomSummary={calculateSpesa}>
          <TotalItem
            name="Spesa"
            summaryType="custom"
            customizeText={(itemInfo: any) => {
              const value: { vitto: number; alloggio: number; trasferta: number } = itemInfo.value
              return `VITTO € ${value.vitto.toLocaleString('en-us', { minimumFractionDigits: 2 })} - ALLOGGIO € ${value.alloggio.toLocaleString('en-us', { minimumFractionDigits: 2 })} - TRASFERTA € ${value.trasferta.toLocaleString('en-us', { minimumFractionDigits: 2 })}`
            }}
            showInColumn="totale"
          />
        </Summary>
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRefSpese.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="groupPanel" />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              onClick: () => {
                navigate(`new`)
              },
              hint: 'Nuova spesa',
              text: 'Nuova spesa',
              icon: 'add',
              stylingMode: 'text',
            }}
          />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Elimina spesa',
              text: 'Elimina spesa',
              icon: 'trash',
              onClick: () => {
                onDeleteExpenseClick()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="columnChooserButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
          />
          <Item
            name="exportButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
          />
          <Item name="searchPanel" />
        </Toolbar>
      </DataGrid>
    </>
  )
}

export default ExpensesPage
